/**
 * Chip builders / parsers for non–property-admin list tables (invoices, activity log, mail, users, etc.).
 */

import { normalizeStringArray } from './propertyListActiveFilterChips';
import { getInvoiceDisputedFilterLabel } from './invoiceListQueryFilters';
import { getInvoiceDateRangeFilterLabel } from './invoiceDateRangeFilter';

function formatSingleDateLabel(value) {
  if (value == null || value === '') return '';
  try {
    const t = new Date(value).getTime();
    if (!Number.isFinite(t)) return String(value);
    return new Date(value).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return String(value);
  }
}

function encSeg(v) {
  return encodeURIComponent(String(v ?? ''));
}

function decSeg(enc) {
  try {
    return decodeURIComponent(enc);
  } catch {
    return enc;
  }
}

export const INVOICE_DUE_DATE_CHIP_ID = 'invoiceDueDate';
export const INVOICE_INVOICE_DATE_CHIP_ID = 'invoiceInvoiceDate';
export const ORG_INVITED_DATE_CHIP_ID = 'orgInvitedDate';
export const USER_ROLE_INVITED_DATE_CHIP_ID = 'userRoleInvitedDate';

const PAYMENT_LABELS = { 1: 'Paid', '-1': 'Unpaid' };

export function buildInvoiceListFilterChips({
  selectedPayment,
  selectedInvoiceStatus,
  selectedDueDateRange,
  selectedInvoiceDateRange,
  selectedDisputed,
}) {
  const chips = [];
  normalizeStringArray(selectedPayment).forEach((v) => {
    chips.push({
      id: `payment:${v}`,
      label: `Payment: ${PAYMENT_LABELS[v] ?? v}`,
    });
  });
  normalizeStringArray(selectedInvoiceStatus).forEach((v) => {
    chips.push({ id: `invoiceStatus:${encSeg(v)}`, label: `Invoice status: ${v}` });
  });
  const dueDateLabel = getInvoiceDateRangeFilterLabel(selectedDueDateRange);
  if (dueDateLabel) {
    chips.push({
      id: INVOICE_DUE_DATE_CHIP_ID,
      label: `Due date: ${dueDateLabel}`,
    });
  }
  const invoiceDateLabel = getInvoiceDateRangeFilterLabel(selectedInvoiceDateRange);
  if (invoiceDateLabel) {
    chips.push({
      id: INVOICE_INVOICE_DATE_CHIP_ID,
      label: `Invoice date: ${invoiceDateLabel}`,
    });
  }
  normalizeStringArray(selectedDisputed).forEach((v) => {
    chips.push({
      id: `disputed:${encSeg(v)}`,
      label: `Disputed: ${getInvoiceDisputedFilterLabel(v)}`,
    });
  });
  return chips;
}

export function parseInvoiceListFilterChipId(chipId) {
  if (chipId === INVOICE_DUE_DATE_CHIP_ID) return { type: 'dueDateRange' };
  if (chipId === INVOICE_INVOICE_DATE_CHIP_ID) return { type: 'invoiceDateRange' };
  if (chipId.startsWith('payment:'))
    return { type: 'payment', value: chipId.slice('payment:'.length) };
  if (chipId.startsWith('invoiceStatus:')) {
    return { type: 'invoiceStatus', value: decSeg(chipId.slice('invoiceStatus:'.length)) };
  }
  if (chipId.startsWith('disputed:')) {
    return { type: 'disputed', value: decSeg(chipId.slice('disputed:'.length)) };
  }
  return null;
}

export function buildActivityLogListFilterChips({ selectedUserStatus, selectedRole, roleLabels }) {
  const chips = [];
  const labels = roleLabels || {};
  (Array.isArray(selectedUserStatus) ? selectedUserStatus : []).forEach((v) => {
    const key = v === true || v === 'true' ? 'true' : 'false';
    chips.push({
      id: `userStatus:${key}`,
      label: `User status: ${key === 'true' ? 'Online' : 'Offline'}`,
    });
  });
  normalizeStringArray(selectedRole).forEach((v) => {
    chips.push({ id: `role:${encSeg(v)}`, label: `Role: ${labels[v] ?? v}` });
  });
  return chips;
}

export function parseActivityLogListFilterChipId(chipId) {
  if (chipId.startsWith('userStatus:')) {
    return { type: 'userStatus', value: chipId.slice('userStatus:'.length) === 'true' };
  }
  if (chipId.startsWith('role:')) {
    return { type: 'role', value: decSeg(chipId.slice('role:'.length)) };
  }
  return null;
}

export function buildMailboxListFilterChips({
  selectedFromEmail,
  selectedToEmail,
  selectedProperty,
}) {
  const chips = [];
  normalizeStringArray(selectedFromEmail).forEach((v) => {
    chips.push({ id: `fromEmail:${encSeg(v)}`, label: `From: ${v}` });
  });
  normalizeStringArray(selectedToEmail).forEach((v) => {
    chips.push({ id: `toEmail:${encSeg(v)}`, label: `To: ${v}` });
  });
  normalizeStringArray(selectedProperty).forEach((v) => {
    chips.push({ id: `property:${encSeg(v)}`, label: `Property: ${v}` });
  });
  return chips;
}

export function parseMailboxListFilterChipId(chipId) {
  if (chipId.startsWith('fromEmail:')) {
    return { type: 'fromEmail', value: decSeg(chipId.slice('fromEmail:'.length)) };
  }
  if (chipId.startsWith('toEmail:')) {
    return { type: 'toEmail', value: decSeg(chipId.slice('toEmail:'.length)) };
  }
  if (chipId.startsWith('property:')) {
    return { type: 'property', value: decSeg(chipId.slice('property:'.length)) };
  }
  return null;
}

export function buildNotificationListFilterChips({
  selectedFromEmail,
  selectedToEmail,
  selectedProperty,
  selectedEmailType,
  selectedInviteType,
  emailTypeOptions,
  inviteTypeOptions,
}) {
  const chips = buildMailboxListFilterChips({
    selectedFromEmail,
    selectedToEmail,
    selectedProperty,
  });
  const emailTypeText = (v) =>
    emailTypeOptions?.find((o) => o.value === v)?.text ?? String(v ?? '');
  const inviteTypeText = (v) =>
    inviteTypeOptions?.find((o) => o.value === v)?.text ?? String(v ?? '');
  normalizeStringArray(selectedEmailType).forEach((v) => {
    chips.push({
      id: `emailType:${encSeg(v)}`,
      label: `Email status: ${emailTypeText(v)}`,
    });
  });
  normalizeStringArray(selectedInviteType).forEach((v) => {
    chips.push({
      id: `inviteType:${encSeg(v)}`,
      label: `Invite type: ${inviteTypeText(v)}`,
    });
  });
  return chips;
}

export function parseNotificationListFilterChipId(chipId) {
  const mailbox = parseMailboxListFilterChipId(chipId);
  if (mailbox) return mailbox;
  if (chipId.startsWith('emailType:')) {
    return { type: 'emailType', value: decSeg(chipId.slice('emailType:'.length)) };
  }
  if (chipId.startsWith('inviteType:')) {
    return { type: 'inviteType', value: decSeg(chipId.slice('inviteType:'.length)) };
  }
  return null;
}

export function buildOrgLandlordFilterChips({
  selectedBuilings,
  selectedUnits,
  selectedInvitedDate,
}) {
  const chips = [];
  normalizeStringArray(selectedBuilings).forEach((v) => {
    chips.push({ id: `propertyCount:${encSeg(v)}`, label: `Property Count: ${v}` });
  });
  normalizeStringArray(selectedUnits).forEach((v) => {
    chips.push({ id: `units:${encSeg(v)}`, label: `Unit Count: ${v}` });
  });
  if (selectedInvitedDate != null && String(selectedInvitedDate).trim() !== '') {
    chips.push({
      id: ORG_INVITED_DATE_CHIP_ID,
      label: `Invited date: ${formatSingleDateLabel(selectedInvitedDate)}`,
    });
  }
  return chips;
}

export function parseOrgLandlordFilterChipId(chipId) {
  if (chipId === ORG_INVITED_DATE_CHIP_ID) return { type: 'invitedDate' };
  if (chipId.startsWith('propertyCount:')) {
    return { type: 'propertyCount', value: decSeg(chipId.slice('propertyCount:'.length)) };
  }
  if (chipId.startsWith('buildings:')) {
    return { type: 'propertyCount', value: decSeg(chipId.slice('buildings:'.length)) };
  }
  if (chipId.startsWith('units:')) {
    return { type: 'units', value: decSeg(chipId.slice('units:'.length)) };
  }
  return null;
}

export function buildUserByRoleListFilterChips({
  selectedBuilings,
  selectedJobTitle,
  selectedInvitedDate,
  jobTitleOptions,
}) {
  const chips = [];
  normalizeStringArray(selectedBuilings).forEach((v) => {
    chips.push({ id: `propertyCount:${encSeg(v)}`, label: `Property Count: ${v}` });
  });
  const jobText = (v) => jobTitleOptions?.find((o) => o.value === v)?.text ?? String(v ?? '');
  normalizeStringArray(selectedJobTitle).forEach((v) => {
    chips.push({ id: `jobTitle:${encSeg(v)}`, label: `Job title: ${jobText(v)}` });
  });
  if (selectedInvitedDate != null && String(selectedInvitedDate).trim() !== '') {
    chips.push({
      id: USER_ROLE_INVITED_DATE_CHIP_ID,
      label: `Invited date: ${formatSingleDateLabel(selectedInvitedDate)}`,
    });
  }
  return chips;
}

export function parseUserByRoleListFilterChipId(chipId) {
  if (chipId === USER_ROLE_INVITED_DATE_CHIP_ID) return { type: 'invitedDate' };
  if (chipId.startsWith('propertyCount:')) {
    return { type: 'propertyCount', value: decSeg(chipId.slice('propertyCount:'.length)) };
  }
  if (chipId.startsWith('buildings:')) {
    return { type: 'propertyCount', value: decSeg(chipId.slice('buildings:'.length)) };
  }
  if (chipId.startsWith('jobTitle:')) {
    return { type: 'jobTitle', value: decSeg(chipId.slice('jobTitle:'.length)) };
  }
  return null;
}
